/** @file INFO for 2-bone IK chains (§6.5): non-uniform parent scale, off-axis child under stretch. */
import type { BoneDoc, Doc, Id } from '@pose2d/format/types';
import type { CommandContext } from '../commandContext';

/** The parent and child bone records of a 2-bone chain; null for a 1-bone chain. */
function chainPair(doc: Doc, bones: readonly Id[]): [BoneDoc, BoneDoc] | null {
  const [first, second] = bones;
  if (first === undefined || second === undefined) return null;
  const parent = doc.bones[first];
  const child = doc.bones[second];
  return parent && child ? [parent, child] : null;
}

/** INFO at `<at>bones.0` for a non-uniform parent setup scale; at `<at>bones.1` for a child with local y ≠ 0 under stretch. */
export function warnIkChain(doc: Doc, ctx: CommandContext, spec: { bones: readonly Id[]; stretch: boolean; at: string }): void {
  const pair = chainPair(doc, spec.bones);
  if (pair === null) return;
  const [parent, child] = pair;
  if (Math.abs(parent.setup.scaleX) !== Math.abs(parent.setup.scaleY)) {
    const message = `bone "${parent.name}" has a non-uniform setup scale; the 2-bone solve takes its non-uniform path`;
    ctx.warnings.push({ code: 'INFO', message, params: { bone: parent.name }, path: `${spec.at}bones.0` });
  }
  if (spec.stretch && child.setup.y !== 0) {
    const message = `bone "${child.name}" sits off its parent's axis (local y ${child.setup.y}); stretch lengthens the parent along its axis only, so the chain may miss the target`;
    ctx.warnings.push({ code: 'INFO', message, params: { bone: child.name }, path: `${spec.at}bones.1` });
  }
}
